import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import { Period, TimePeriod } from '@/types/date';
import { formatPeriodShortWithYear, getPeriodUntilDate, getPreviousPeriod } from '@/utils/date/timePeriods';

export interface QuickSelectOption {
  id: string;
  title: string;
  primaryText: string;
  secondaryText: string;
  dateRange: Period;
  comparisonPeriod: Period;
}

const getWeekPeriod = (endDate: Dayjs): Period => ({
  beginn: endDate.subtract(6, 'day'),
  end: endDate,
  period: TimePeriod.WEEK,
});

const getMonthPeriod = (endDate: Dayjs): Period => ({
  beginn: endDate.subtract(29, 'day'),
  end: endDate,
  period: TimePeriod.MONTH,
});

const getQuarterPeriod = (endDate: Dayjs): Period => ({
  beginn: endDate.subtract(89, 'day'),
  end: endDate,
  period: TimePeriod.QUARTER,
});

const getYTDPeriod = (endDate: Dayjs): Period => ({
  beginn: endDate.startOf('year'),
  end: endDate,
  period: TimePeriod.YTD,
});

const getYearPeriod = (endDate: Dayjs): Period => ({
  beginn: endDate.subtract(364, 'day'),
  end: endDate,
  period: TimePeriod.YEAR,
});

const getPreviousWeekPeriod = (currentPeriod: Period): Period => ({
  beginn: currentPeriod.beginn.subtract(7, 'day'),
  end: currentPeriod.end.subtract(7, 'day'),
  period: TimePeriod.WEEK,
});

const getPreviousMonthPeriod = (currentPeriod: Period): Period => ({
  beginn: currentPeriod.beginn.subtract(30, 'day'),
  end: currentPeriod.end.subtract(30, 'day'),
  period: TimePeriod.MONTH,
});

const getPreviousQuarterPeriod = (currentPeriod: Period): Period => ({
  beginn: currentPeriod.beginn.subtract(90, 'day'),
  end: currentPeriod.end.subtract(90, 'day'),
  period: TimePeriod.QUARTER,
});

const getPreviousYTDPeriod = (currentPeriod: Period): Period => ({
  beginn: currentPeriod.beginn.subtract(1, 'year'),
  end: currentPeriod.end.subtract(1, 'year'),
  period: TimePeriod.YTD,
});

const getPreviousYearPeriod = (currentPeriod: Period): Period => ({
  beginn: currentPeriod.beginn.subtract(365, 'day'),
  end: currentPeriod.end.subtract(365, 'day'),
  period: TimePeriod.YEAR,
});

export const getQuickSelectOptions = (t: TFunction, referenceDate: Dayjs = dayjs()): QuickSelectOption[] => {
  const weekPeriod = getWeekPeriod(referenceDate);
  const monthPeriod = getMonthPeriod(referenceDate);
  const quarterPeriod = getQuarterPeriod(referenceDate);
  const cycleToDatePeriod = getPeriodUntilDate(referenceDate, TimePeriod.CYCLE);
  const ytdPeriod = getYTDPeriod(referenceDate);
  const yearPeriod = getYearPeriod(referenceDate);

  return [
    {
      id: 'last-7-days',
      title: t('header.navigation.dateRange.quickSelect.last7Days.title'),
      primaryText: formatPeriodShortWithYear(weekPeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.last7Days.secondaryText'),
      dateRange: weekPeriod,
      comparisonPeriod: getPreviousWeekPeriod(weekPeriod),
    },
    {
      id: 'last-30-days',
      title: t('header.navigation.dateRange.quickSelect.last30Days.title'),
      primaryText: formatPeriodShortWithYear(monthPeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.last30Days.secondaryText'),
      dateRange: monthPeriod,
      comparisonPeriod: getPreviousMonthPeriod(monthPeriod),
    },
    {
      id: 'last-90-days',
      title: t('header.navigation.dateRange.quickSelect.last90Days.title'),
      primaryText: formatPeriodShortWithYear(quarterPeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.last90Days.secondaryText'),
      dateRange: quarterPeriod,
      comparisonPeriod: getPreviousQuarterPeriod(quarterPeriod),
    },
    {
      id: 'cycle-to-date',
      title: t('header.navigation.dateRange.quickSelect.cycleToDate.title'),
      primaryText: formatPeriodShortWithYear(cycleToDatePeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.cycleToDate.secondaryText'),
      dateRange: cycleToDatePeriod,
      comparisonPeriod: getPreviousPeriod(cycleToDatePeriod),
    },
    {
      id: 'ytd',
      title: t('header.navigation.dateRange.quickSelect.ytd.title'),
      primaryText: formatPeriodShortWithYear(ytdPeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.ytd.secondaryText'),
      dateRange: ytdPeriod,
      comparisonPeriod: getPreviousYTDPeriod(ytdPeriod),
    },
    {
      id: 'last-365-days',
      title: t('header.navigation.dateRange.quickSelect.last365Days.title'),
      primaryText: formatPeriodShortWithYear(yearPeriod),
      secondaryText: t('header.navigation.dateRange.quickSelect.last365Days.secondaryText'),
      dateRange: yearPeriod,
      comparisonPeriod: getPreviousYearPeriod(yearPeriod),
    },
  ];
};
